import { pricePlans } from "./prices";

// Enhanced similarity calculation using Jaro-Winkler distance
export const calculateSimilarity = (str1, str2) => {
  const cleanStr1 = str1.toLowerCase().replace(/[.,?!]/g, '').trim();
  const cleanStr2 = str2.toLowerCase().replace(/[.,?!]/g, '').trim();

  if (cleanStr1 === cleanStr2) return 1.0;

  const matchWindow = Math.floor(Math.max(cleanStr1.length, cleanStr2.length) / 2) - 1;
  const matches1 = new Array(cleanStr1.length).fill(false);
  const matches2 = new Array(cleanStr2.length).fill(false);

  let matches = 0;
  let transpositions = 0;

  for (let i = 0; i < cleanStr1.length; i++) {
    const start = Math.max(0, i - matchWindow);
    const end = Math.min(i + matchWindow + 1, cleanStr2.length);

    for (let j = start; j < end; j++) {
      if (!matches2[j] && cleanStr1[i] === cleanStr2[j]) {
        matches1[i] = true;
        matches2[j] = true;
        matches++;
        break;
      }
    }
  }

  if (matches === 0) return 0.0;

  let k = 0;
  for (let i = 0; i < cleanStr1.length; i++) {
    if (matches1[i]) {
      while (!matches2[k]) k++;
      if (cleanStr1[i] !== cleanStr2[k]) transpositions++;
      k++;
    }
  }

  const jaro = ((matches / cleanStr1.length) + (matches / cleanStr2.length) + ((matches - transpositions / 2) / matches)) / 3.0;
  const winkler = jaro + (Math.min(4, commonPrefixLength(cleanStr1, cleanStr2)) * 0.1 * (1 - jaro));

  return winkler;
};

const commonPrefixLength = (str1, str2) => {
  const maxLength = Math.min(str1.length, str2.length);
  for (let i = 0; i < maxLength; i++) {
    if (str1[i] !== str2[i]) return i;
  }
  return maxLength;
};

// Enhanced keyword extraction with stemming
export const extractKeywords = (text) => {
  const stopWords = new Set([
    'a', 'an', 'and', 'are', 'as', 'at', 'be', 'by', 'for', 'from', 
    'has', 'he', 'in', 'is', 'it', 'its', 'of', 'on', 'that', 'the', 
    'to', 'was', 'were', 'will', 'with', 'what', 'where', 'when', 'who', 
    'how', 'why', 'which', 'do', 'does', 'can', 'could', 'would', 'should', 
    'tell', 'me', 'about', 'i', 'want', 'need', 'please', 'your', 'you', 
    'know', 'let', 'give', 'have', 'had', 'may', 'might', 'must'
  ]);

  return text
    .toLowerCase()
    .replace(/[.,?!]/g, '')
    .split(' ')
    .filter(word => word.length > 2 && !stopWords.has(word))
    .map(word => stemWord(word));
};

const stemWord = (word) => {
  // Basic stemming for common suffixes
  if (word.length > 4) {
    if (word.endsWith('ing')) return word.slice(0, -3);
    if (word.endsWith('ed')) return word.slice(0, -2);
    if (word.endsWith('es')) return word.slice(0, -2);
    if (word.endsWith('s')) return word.slice(0, -1);
  }
  return word;
};

// Enhanced matching logic with weighted keywords
export const findBestMatch = (userInput, faqQuestions) => {
  const threshold = 0.4; // Increased threshold for better accuracy
  let bestMatch = null;
  let bestScore = 0;

  const cleanInput = userInput.toLowerCase().trim();
  const inputKeywords = extractKeywords(cleanInput);

  for (const faq of faqQuestions) {
    const question = faq.question.toLowerCase();
    const questionKeywords = extractKeywords(question);

    const simScore = calculateSimilarity(cleanInput, question);
    const keywordMatch = inputKeywords.filter(k => 
      questionKeywords.some(qk => qk.includes(k) || k.includes(qk))
    ).length / Math.max(inputKeywords.length, questionKeywords.length);

    const finalScore = (simScore * 0.7) + (keywordMatch * 0.3); // Adjusted weights

    if (finalScore > bestScore && finalScore >= threshold) {
      bestScore = finalScore;
      bestMatch = faq;
    }
  }

  return bestMatch;
};

// Enhanced topic matching with weighted scores
const topicMatches = {
  greeting: { keywords: ['hi', 'hello', 'hey', 'greetings', 'welcome'], weight: 1.0, responseKey: 'greeting' },
  marketing: { keywords: ['marketing', 'strategy', 'campaign', 'promote', 'advertising'], weight: 0.9, responseKey: 'marketing' },
  services: { keywords: ['service', 'offer', 'provide', 'help', 'support', 'assistance'], weight: 0.9, responseKey: 'services' },
  seo: { keywords: ['seo', 'search', 'ranking', 'google', 'optimization', 'keyword'], weight: 0.95, responseKey: 'seo' },
  social: { keywords: ['social', 'facebook', 'instagram', 'twitter', 'linkedin', 'tiktok', 'platform'], weight: 0.9, responseKey: 'social' },
  content: { keywords: ['content', 'blog', 'article', 'post', 'write', 'writing'], weight: 0.85, responseKey: 'content' },
  ppc: { keywords: ['ppc', 'ads', 'advertising', 'google ads', 'paid', 'campaign'], weight: 0.9, responseKey: 'ppc' },
  analytics: { keywords: ['analytics', 'metrics', 'data', 'tracking', 'measure', 'report'], weight: 0.85, responseKey: 'analytics' },
  email: { keywords: ['email', 'newsletter', 'campaign', 'mailchimp', 'subscriber'], weight: 0.85, responseKey: 'email' },
  branding: { keywords: ['brand', 'identity', 'logo', 'design', 'image'], weight: 0.9, responseKey: 'branding' },
  conversion: { keywords: ['conversion', 'rate', 'optimize', 'cro', 'sales', 'leads'], weight: 0.9, responseKey: 'conversion' },
  influencer: { keywords: ['influencer', 'collaboration', 'partner', 'sponsorship'], weight: 0.85, responseKey: 'influencer' },
  video: { keywords: ['video', 'youtube', 'tiktok', 'content', 'production'], weight: 0.85, responseKey: 'video' },
  mobile: { keywords: ['mobile', 'app', 'responsive', 'phone', 'tablet'], weight: 0.85, responseKey: 'mobile' },
  local: { keywords: ['local', 'business', 'gmb', 'maps', 'location'], weight: 0.9, responseKey: 'local' },
  ecommerce: { keywords: ['ecommerce', 'shop', 'store', 'product', 'sales', 'shopify', 'woocommerce'], weight: 0.95, responseKey: 'ecommerce' },
  webdevelopment: { keywords: ['website', 'development', 'web', 'web development', 'tools'], weight: 0.95, responseKey: 'web development' },
  about: { keywords: ['about', 'company', 'who', 'team', 'background', 'history'], weight: 0.9, responseKey: 'about' },
  contact: { keywords: ['contact', 'reach', 'email', 'phone', 'address', 'location', 'office'], weight: 0.9, responseKey: 'contact' }
};

export const getEnhancedResponse = (input, responses) => {
  const text = input.toLowerCase();
  const keywords = extractKeywords(text);

  // Check for pricing-related keywords
  const pricingKeywords = ['price', 'cost', 'pricing', 'how much', 'package', 'plan'];
  const isPricingQuery = pricingKeywords.some(keyword => text.includes(keyword));

  if (isPricingQuery) {
    // Check for specific service keywords
    if (keywords.some(k => k.includes('web') || k.includes('website'))) {
      return responses.webPricing[0];
    } else if (keywords.some(k => k.includes('seo') || k.includes('search'))) {
      return responses.seoPricing[0];
    } else if (keywords.some(k => k.includes('brand') || k.includes('logo'))) {
      return responses.brandingPricing[0];
    } else if (keywords.some(k => k.includes('marketing') || k.includes('digital'))) {
      return responses.marketingPricing[0];
    } else {
      return responses.pricing[0]; // General pricing response
    }
  }

  const faqMatch = findBestMatch(text, responses.faq);
  if (faqMatch) return faqMatch.answer;

  const topicScores = Object.entries(topicMatches).map(([topic, config]) => {
    const matchCount = keywords.filter(keyword => 
      config.keywords.some(tk => keyword.includes(tk) || tk.includes(keyword))
    ).length;

    return {
      topic,
      score: (matchCount / keywords.length) * config.weight,
      responseKey: config.responseKey
    };
  });

  const bestMatch = topicScores
    .filter(match => match.score > 0)
    .sort((a, b) => b.score - a.score)[0];

  if (bestMatch && responses[bestMatch.responseKey]) {
    const responseArray = responses[bestMatch.responseKey];
    return responseArray[Math.floor(Math.random() * responseArray.length)];
  }

  return responses.default[Math.floor(Math.random() * responses.default.length)];
};

// Enhanced plan finding with partial matching and ranking
export const findPlansByKeywords = (searchTerm) => {
  const search = searchTerm.toLowerCase();
  const matches = [];

  pricePlans.forEach(service => {
    const serviceMatch = service.keywords.some(keyword => 
      keyword.toLowerCase().includes(search) || 
      search.includes(keyword.toLowerCase())
    );

    service.plans.forEach(plan => {
      const planMatch = plan.keywords.some(keyword => 
        keyword.toLowerCase().includes(search) || 
        search.includes(keyword.toLowerCase())
      );

      if (serviceMatch || planMatch) {
        const matchScore = calculateSimilarity(search, plan.name.toLowerCase());
        matches.push({
          service: service.service,
          plan: plan.name,
          price: plan.price,
          features: plan.features,
          score: matchScore
        });
      }
    });
  });

  return matches
    .sort((a, b) => b.score - a.score)
    .map(match => ({
      service: match.service,
      plan: match.plan,
      price: match.price,
      features: match.features
    }));
};